import { FC, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import IRisk from '@models/IRisk';
import { motion } from 'framer-motion';
import { lighten, Stack, Typography } from '@mui/material';
import { RiskLevel } from '../../../shared/types';
import { GREY } from '../../../colors';
import { useGlobalDrawerContext } from '../GlobalDrawer';
import { RiskType, Size } from './types';
import { ExtendedRiskIndicator } from './ExtendedRiskIndicator';
// eslint-disable-next-line import/no-cycle
import { determineRiskType } from './functions';
import { Loading } from '../Loading';
import { ExtremeRiskIcon } from '../ExtremeRiskIcon';

interface RiskIndicatorProps {
  drawerTitle?: string;
  risks: IRisk[];
  icon?: string;
  size?: Size;
  onYChange?: (b: DOMRect) => void;
  withRouting?: boolean;
  trigger?: 'click' | 'hover';
  withDrawer?: boolean;
  onClick?: () => void;
  simplified?: boolean;
  loading?: boolean;
}

export const RiskIndicator: FC<RiskIndicatorProps> = ({
  drawerTitle,
  risks,
  icon = 'bi:exclamation-circle',
  size = 'small',
  onYChange,
  withRouting,
  trigger = 'hover',
  withDrawer = true,
  onClick,
  simplified,
  loading,
}) => {
  const [riskType, setRiskType] = useState<RiskType>({
    level: RiskLevel.None,
    color: GREY,
  });

  const [hoverStart, setHoverStart] = useState<boolean>(false);

  useEffect(() => {
    setRiskType(determineRiskType(risks));
  }, [risks]);

  const { showDrawer } = useGlobalDrawerContext();
  useEffect(() => {
    if (risks?.length > 0) {
      if (riskType.level !== RiskLevel.None) {
        if (hoverStart && withDrawer) {
          showDrawer(
            `${drawerTitle}-risk`,
            (lastId: string, closed: boolean) => {
              if (closed && `${drawerTitle}-risk` === lastId) {
                setHoverStart(false);
              } else if (`${drawerTitle}-risk` !== lastId) {
                setHoverStart(false);
              } else {
                setHoverStart(true);
              }
            },
            risks[0].level === 'Not-Found' ? (
              <Typography>{'Not Found'}</Typography>
            ) : (
              <ExtendedRiskIndicator
                onYChange={onYChange}
                riskType={riskType}
                risks={risks}
                title={drawerTitle}
                size={size}
                withRouting={withRouting}
              />
            ),
          );
        }
      }
    } else if (loading) {
      showDrawer(
        `loading-risk-0`,
        (lastId: string, closed: boolean) => {
          if (closed && lastId === `loading-risk-0`) {
            setHoverStart(false);
          } else if (lastId !== `loading-risk-0`) {
            setHoverStart(false);
          } else {
            setHoverStart(true);
          }
        },
        <Stack sx={{ pt: 8 }}>
          <Loading />
        </Stack>,
      );
    }
  }, [hoverStart, riskType, drawerTitle, size, risks]);

  const getIcon = () => {
    if (!hoverStart || simplified) {
      return icon;
    }

    if (riskType.level === RiskLevel.None || !withDrawer) {
      return icon;
    }

    return 'bi:shield-fill-exclamation';
  };

  const determineFontSize = (size: Size) => {
    switch (size) {
      case 'small':
        return 40;
      case 'medium':
        return 50;
      case 'big':
        return 65;
      default:
        return 50;
    }
  };

  return (
    <>
      <motion.div
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        transition={{ delay: 0.1 }}
        whileHover={{
          scale: riskType.level !== RiskLevel.None || simplified ? 1.1 : 1,
        }}
        onHoverStart={() => {
          if (trigger === 'hover') {
            setHoverStart(true);
          }
        }}
        onClick={(event) => {
          event.stopPropagation();
          if (trigger === 'click' || onClick) {
            if (onClick) {
              onClick();
            }
            setHoverStart(true);
          }
        }}
      >
        {riskType.level === RiskLevel.Extreme && !simplified ? (
          <ExtremeRiskIcon fontSize={determineFontSize(size)} />
        ) : (
          <Icon
            icon={getIcon()}
            color={
              // eslint-disable-next-line no-nested-ternary
              simplified
                ? hoverStart
                  ? lighten(riskType.color, 0.2)
                  : 'white'
                : riskType.color
            }
            fontSize={determineFontSize(size)}
          />
        )}
      </motion.div>
    </>
  );
};
